import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Icon,
  StackLayout,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useMasonryEngineActionEmitter } from '@leagueplatform/masonry-engine';
import {
  FILTERS_CHANGED_EVENT,
  removeSearchParam,
} from 'utils/replace-search-param.util';
import {
  RESOLVER_PARAM_BY_FIELD,
  RELOAD_NODE_ID,
  FILTER_BAR_NODE_ID,
  getSetFilterAction,
  getClearFiltersAction,
} from './scfhp-select-input.renderer';

// Active-filter chips + Clear All for the submitted-requests page (web).
// Mounted on a `box` node in the `scfhp_form_status_filter` namespace. Applied
// filters live in the URL query params (written by the dropdowns); this reads
// them and re-renders whenever they change, so changing a dropdown immediately
// shows/updates the chips. Removing a chip / Clear All clears the params and
// reloads the list + the dropdowns via the shared setFilter/clearFilters path.

// Theme colours bridged to CSS vars in index.tsx (no hardcoded hex).
const TEAL = 'var(--scfhp-interactive-action-primary)';
const TEXT_PRIMARY = 'var(--scfhp-onsurface-text-primary)';
const CHIP_BG = 'var(--scfhp-surface-background-secondary)';

const FIELD_ORDER = ['formType', 'statusCode', 'submissionDate'] as const;

// Mirrors the option labels in formNameFilter / statusFilter /
// submissionDateFilter specs.
const CHIP_LABELS: Record<string, Record<string, string>> = {
  formType: {
    TRANSPORTATION_REQUEST: 'Transportation Request',
    NEW_MEMBER_ID_CARD: 'New Member ID Card Request',
    PCP_CHANGE: 'PCP Change Request',
    CONTACT_INFO_CHANGE: 'Change Contact Info Request',
    COMMUNICATION_PREFERENCES: 'Update Communication Preferences',
    CASE_MANAGEMENT: 'Case Management Request',
    HEALTH_EDUCATION: 'Health Education Request',
  },
  statusCode: {
    RECEIVED: 'Received',
    IN_REVIEW: 'Pending Review',
    SCHEDULED: 'Scheduled',
    COMPLETED: 'Completed',
    DUPLICATE: 'Duplicate',
    UNABLE_TO_PROCESS: 'Unable to Process',
  },
  submissionDate: {
    LAST_7_DAYS: 'Last 7 days',
    LAST_30_DAYS: 'Last 30 days',
    LAST_90_DAYS: 'Last 90 days',
    LAST_YEAR: 'Last year',
  },
};

const RELOAD_NODE_IDS = [RELOAD_NODE_ID, FILTER_BAR_NODE_ID];

type ActiveChip = {
  fieldId: string;
  paramKey: string;
  value: string;
  label: string;
};

const readActiveChips = (): ActiveChip[] => {
  const params = new URLSearchParams(window.location.search);
  const chips: ActiveChip[] = [];
  FIELD_ORDER.forEach((fieldId) => {
    const paramKey = RESOLVER_PARAM_BY_FIELD[fieldId];
    const value = params.get(paramKey) ?? '';
    if (!value) return;
    chips.push({
      fieldId,
      paramKey,
      value,
      label: CHIP_LABELS[fieldId]?.[value] ?? value,
    });
  });
  return chips;
};

export const ScfhpFilterChipsRenderer = () => {
  const invokeAction = useMasonryEngineActionEmitter();
  const [chips, setChips] = useState<ActiveChip[]>(readActiveChips);

  // Re-read the active filters whenever the URL params change (dropdown change,
  // chip removal, Clear All, or back/forward).
  useEffect(() => {
    const sync = () => setChips(readActiveChips());
    window.addEventListener(FILTERS_CHANGED_EVENT, sync);
    window.addEventListener('popstate', sync);
    return () => {
      window.removeEventListener(FILTERS_CHANGED_EVENT, sync);
      window.removeEventListener('popstate', sync);
    };
  }, []);

  const removeChip = useCallback(
    (paramKey: string) => {
      removeSearchParam(paramKey);
      RELOAD_NODE_IDS.forEach((nodeId) =>
        invokeAction(getSetFilterAction(nodeId, paramKey, '')),
      );
    },
    [invokeAction],
  );

  const clearAll = useCallback(() => {
    Object.values(RESOLVER_PARAM_BY_FIELD).forEach(removeSearchParam);
    RELOAD_NODE_IDS.forEach((nodeId) =>
      invokeAction(getClearFiltersAction(nodeId)),
    );
  }, [invokeAction]);

  if (chips.length === 0) return null;

  return (
    <StackLayout
      orientation="horizontal"
      spacing="$half"
      css={{ flexWrap: 'wrap', alignItems: 'center' }}
    >
      {chips.map((chip) => (
        <Box
          as="button"
          type="button"
          key={chip.paramKey}
          onClick={() => removeChip(chip.paramKey)}
          aria-label={`Remove ${chip.label} filter`}
          css={{
            display: 'inline-flex',
            alignItems: 'center',
            height: '32px',
            gap: '$half',
            cursor: 'pointer',
            backgroundColor: CHIP_BG,
            border: `1px solid ${TEAL}`,
            borderRadius: '8px',
            paddingBlock: '$quarter',
            paddingInline: '$half',
            color: TEXT_PRIMARY,
          }}
        >
          <UtilityText
            css={{ color: TEXT_PRIMARY, fontSize: '14px', fontWeight: 400 }}
          >
            {chip.label}
          </UtilityText>
          <Icon icon="tinyClose" size={10} css={{ color: TEAL }} />
        </Box>
      ))}
      <Button
        priority="tertiary"
        onClick={clearAll}
        css={{
          color: TEAL,
          border: 'none',
          backgroundColor: 'transparent',
          fontSize: '16px',
          fontWeight: 700,
          paddingInline: '$half',
        }}
      >
        Clear All
      </Button>
    </StackLayout>
  );
};
